import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useListener } from "@/context/ListenerContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import ListenerAuthDialog from "@/components/ListenerAuthDialog";
import { AUDIOBOOKS_ENABLED } from "@/config/features";
import { Feather, Sun, Moon, ArrowRight, Library, Headphones } from "lucide-react";

export const MarketingNav = ({ children }) => {
  const { user } = useAuth();
  const { listener } = useListener();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [listenerAuthOpen, setListenerAuthOpen] = useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="marketing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-6 px-5 sm:px-8 h-16">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="marketing-logo">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Feather className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">CodexStudio</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground ml-4">
          {children}
        </nav>

        <div className="flex-1" />

        {AUDIOBOOKS_ENABLED && ((listener || user) ? (
          <Link
            to="/library"
            data-testid="marketing-my-library"
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Library className="w-4 h-4" /> <span className="hidden sm:inline">My Library</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setListenerAuthOpen(true)}
            data-testid="marketing-listener-signin"
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Headphones className="w-4 h-4" /> <span className="hidden sm:inline">My Library</span>
          </button>
        ))}

        <button
          onClick={toggle}
          data-testid="marketing-theme-toggle"
          className="w-9 h-9 rounded-lg grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {user ? (
          <Button data-testid="marketing-open-studio" onClick={() => navigate("/app")} className="gap-1.5 font-semibold">
            Open studio <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button data-testid="marketing-signin" onClick={() => navigate("/auth")} className="gap-1.5 font-semibold">
            Sign in <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>
      {AUDIOBOOKS_ENABLED && <ListenerAuthDialog open={listenerAuthOpen} onOpenChange={setListenerAuthOpen} redirectTo="/library" />}
    </header>
  );
};
